// src/components/layout/navbar/CartPanel/CartPanel.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../../context/CartContext";
import { useStoreSettings } from "../../../../hooks/useStoreSettings";
import { useLanguage } from "../../../../context/LanguageContext";
import useCurrency from "../../../../hooks/useCurrency";
import "./CartPanel.css";
import OrderItemsList from "../../../common/OrderItemList/OrderItemList";
import WishlistToggle from "../../../common/WishlistToggle/WishlistToggle";

const CartPanel = ({ className, isVisible = true }) => {
  const { toggleCart } = useCart();
  const onClose = () => toggleCart(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const {
    cart,
    loading,
    updateQuantity,
    removeItem,
    formatCurrencyNumber,  // Use the number-only formatter for OrderItemsList
    refreshCart
  } = useCart();

  // Translations for cart panel
  const translations = {
    en: {
      title: "My Shopping Bag",
      subtotal: "Subtotal",
      shippingNote: "Shipping and taxes calculated at checkout.",
      proceedToCheckout: "Proceed To Checkout",
      continueShopping: "Continue Shopping"
    },
    ar: {
      title: "حقيبة التسوق",
      subtotal: "المجموع الفرعي",
      shippingNote: "سيتم حساب الشحن والضرائب عند الدفع.",
      proceedToCheckout: "المتابعة للدفع",
      continueShopping: "متابعة التسوق"
    }
  };

  const t = translations[language] || translations.en;

  // Get store settings for currency
  const { currency: storeCurrency } = useStoreSettings();

  // Use centralized currency formatting
  const { formatDisplay, getCurrency } = useCurrency();

  // Prevent body scroll when cart is visible
  useEffect(() => {
    if (isVisible) {
      document.body.classList.add("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isVisible]);

  const handleQuantityChange = async (productId, newQuantity, variantId = null) => {
    await updateQuantity(productId, newQuantity, variantId);
  };

  const handleRemoveItem = async (productId, variantId = null) => {
    await removeItem(productId, variantId);
  };

  const handleItemClick = (item) => {
    // Close the cart panel first
    onClose();

    // Navigate to item detail page using the product ID
    // You can modify this to use a slug if your products have slugs
    navigate(`/product/${item.productId || item.id}`);
  };

  return (
    <div className={`cart-panel ${className}`}>
      <div className="cart-header">
        <h2>{t.title}</h2>
        <button className="close-cart" onClick={onClose}>×</button>
      </div>

      <div className="cart-body">
        <OrderItemsList
          items={cart.items}
          currency={getCurrency()}
          formatCurrency={formatCurrencyNumber}
          variant="cart"
          showQuantity={false}
          showVariants={true}
          showItemTotal={true}
          showWishlistToggle={true}
          showRemoveButton={true}
          showQuantityControls={true}
          showDiscountInfo={true}
          WishlistComponent={WishlistToggle}
          loading={loading}
          onItemClick={handleItemClick}
          onQuantityChange={handleQuantityChange}
          onRemoveItem={handleRemoveItem}
          className="order-items-cart"
          disableRTL={true}
        />
      </div>

      <div className="cart-footer">
        <div className="subtotal">
          <span>{t.subtotal}</span>
          <span>{formatDisplay(cart.subtotal)}</span>
        </div>
        <p className="note">{t.shippingNote}</p>
        <button
          className="checkout-btn"
          onClick={() => {
            onClose();
            // Force reload if already on checkout page, otherwise navigate normally
            if (window.location.pathname === "/checkout") {
              window.location.reload();
            } else {
              navigate("/checkout");
            }
          }}
          disabled={loading || cart.items.length === 0}
        >
          {t.proceedToCheckout}
        </button>
        <a href="/shop" className="continue-link" onClick={onClose}>
          {t.continueShopping}
        </a>
      </div>
    </div>
  );
};

export default CartPanel;